import { trackFunnelStep } from '@/utilities/dataLayer/events/funnelStep';
import { trackOtpSubFunnel } from '@/utilities/dataLayer/events/otp';
import { trackPurchase } from '@/utilities/dataLayer/events/purchase';
import { trackPurchaseFailed } from '@/utilities/dataLayer/events/purchaseFailed';
import type { ExtraServiceItem } from '@/utilities/dataLayer/models';

import type { AddressAction, AuthMethod, BatteryPaymentMethod, OtpSubStep } from '@/constants/dataLayer';
import { BATTERY_FUNNELS } from '@/constants/dataLayer';

type BatteryVehicle = 'car' | 'motor';

type WithBatteryVehicle = {
    vehicle: BatteryVehicle;
};

const getFunnel = (vehicle: BatteryVehicle) => BATTERY_FUNNELS[vehicle];

export const trackBatteryVehicleSelection = ({ vehicle, brand, model }: WithBatteryVehicle & { brand: string; model: string }): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.vehicleSelection
        },
        vehicle: {
            brand,
            model
        }
    });
};

export const trackBatterySelection = ({
    vehicle,
    brand,
    model,
    itemId,
    itemName,
    batteryBrand,
    price
}: WithBatteryVehicle & {
    brand: string;
    model: string;
    itemId: string;
    itemName: string;
    batteryBrand: string;
    price: number;
}): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.batterySelection
        },
        vehicle: {
            brand,
            model
        },
        battery: {
            item_id: itemId,
            item_name: itemName,
            brand: batteryBrand,
            price,
            quantity: 1
        }
    });
};

export const trackBatteryIdentityCheck = ({
    vehicle,
    brand,
    model,
    authMethod
}: WithBatteryVehicle & {
    brand: string;
    model: string;
    authMethod: AuthMethod;
}): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.identityCheck
        },
        auth: {
            method: authMethod
        },
        vehicle: {
            brand,
            model
        }
    });
};

export const trackBatteryReservation = ({ vehicle, date, timeSlot }: WithBatteryVehicle & { date: string; timeSlot: string }): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.reservation
        },
        reservation: {
            date,
            time_slot: timeSlot
        }
    });
};

export const trackBatteryAddress = ({ vehicle, action }: WithBatteryVehicle & { action: AddressAction }): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.address
        },
        address: {
            action
        }
    });
};

export const trackBatteryExtraServices = ({ vehicle, selected }: WithBatteryVehicle & { selected: ExtraServiceItem[] }): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.extraServices
        },
        extra_services: {
            selected
        }
    });
};

export const trackBatteryPayment = ({
    vehicle,
    method,
    couponUsed,
    couponCode
}: WithBatteryVehicle & {
    method: BatteryPaymentMethod;
    couponUsed: boolean;
    couponCode?: string;
}): void => {
    const funnel = getFunnel(vehicle);

    trackFunnelStep({
        funnel: {
            funnel_name: funnel.funnelName,
            ...funnel.steps.payment
        },
        payment: {
            method,
            coupon_used: couponUsed,
            ...(couponCode ? { coupon_code: couponCode } : {})
        }
    });
};

export const trackBatteryOtpSubFunnel = ({
    vehicle,
    step,
    brand,
    model
}: WithBatteryVehicle & {
    step: OtpSubStep;
    brand: string;
    model: string;
}): void => {
    const funnel = getFunnel(vehicle);

    trackOtpSubFunnel({
        funnelName: funnel.funnelName,
        step,
        vehicle: {
            brand,
            model
        }
    });
};

export const trackBatteryPurchase = ({
    vehicle,
    transactionId,
    value,
    itemId,
    itemName,
    itemBrand,
    itemPrice,
    brand,
    model
}: WithBatteryVehicle & {
    transactionId: string;
    value: number;
    itemId: string;
    itemName: string;
    itemBrand: string;
    itemPrice: number;
    brand: string;
    model: string;
}): void => {
    const funnel = getFunnel(vehicle);

    trackPurchase({
        transactionId,
        value,
        businessLine: funnel.businessLine,
        items: [
            {
                item_id: itemId,
                item_name: itemName,
                item_brand: itemBrand,
                price: itemPrice,
                quantity: 1
            }
        ],
        vehicle: {
            brand,
            model
        }
    });
};

export const trackBatteryPurchaseFailed = ({
    vehicle,
    transactionId,
    value,
    failureReason
}: WithBatteryVehicle & {
    transactionId: string;
    value: number;
    failureReason: string;
}): void => {
    const funnel = getFunnel(vehicle);

    trackPurchaseFailed({
        transactionId,
        value,
        businessLine: funnel.businessLine,
        failureReason
    });
};
